// Public marketing homepage — sells the vision. Data dashboard lives at /dashboard.
import React from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Binoculars, Scales, PlayCircle, ArrowCounterClockwise, ArrowRight, Brain, UsersThree, Kanban, GitDiff, SoccerBall, ChartLineUp, GraduationCap, GameController, Trophy } from "@phosphor-icons/react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";

const CYCLE = [
    { key: "ANALYZE", icon: MagnifyingGlass, text: "Definuj, jakého hráče hledáš, a objev vhodné kandidáty pomocí AI Recruitera a datových nástrojů." },
    { key: "SCOUT", icon: Binoculars, text: "Vytvoř si vlastní pool hráčů, přidávej poznámky a sleduj jejich vývoj." },
    { key: "SELECT", icon: Scales, text: "Porovnej kandidáty a vyber hráče, který nejlépe odpovídá tvému týmu a plánovanému období." },
    { key: "PLAY", icon: PlayCircle, text: "Vyzkoušej přestupy, sestavy a několik možných scénářů." },
    { key: "REVIEW", icon: ArrowCounterClockwise, text: "Vyhodnoť, jak se dařilo tvým hráčům a zda bylo původní rozhodnutí správné." },
];

const SKILLS = ["Scouting hráčů", "Práce s daty", "Vyhodnocování výkonu", "Práce s rozpočtem", "Rozhodování pod nejistotou", "Plánování sestavy", "Formulování a zpětné hodnocení hypotéz"];

const TOOLS = [
    { icon: Brain, title: "AI Recruiter", text: "Zadej, koho hledáš — AI projde reálná data a doporučí kandidáty s vysvětlením a riziky.", to: "/lab/analyze" },
    { icon: UsersThree, title: "Player Explorer", text: "Jednotný profil hráče: 6 sezón historie, výkon dle síly soupeře, timing, články.", to: "/players" },
    { icon: Kanban, title: "Recruitment Board", text: "Vlastní pool hráčů se statusy Watch → Scouted → Shortlist → Priority.", to: "/lab/scout" },
    { icon: GitDiff, title: "Player Comparison", text: "Porovnej kandidáty podle konkrétního zadání, ne jen obecných čísel.", to: "/lab/select" },
    { icon: SoccerBall, title: "Squad Simulator", text: "Monte-Carlo simulace sestavy a celé sezóny včetně přestupů a kapitána.", to: "/lab/play" },
    { icon: ChartLineUp, title: "Performance Review", text: "Vyhodnocení sestavy, poolu i kvality tvých rozhodnutí za 1 / 3 / 8 GW.", to: "/lab/review" },
];

const Section = ({ children, className = "" }) => (
    <section className={`max-w-6xl mx-auto px-2 md:px-4 ${className}`}>{children}</section>
);

export default function Home() {
    const { user, login } = useAuth();
    return (
        <AppShell>
            <div data-testid="home-page" className="space-y-24 py-6">
                {/* HERO */}
                <Section>
                    <div className="max-w-3xl">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-mint mb-4">Fantasy Academy</div>
                        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Think like a <span className="text-fa-mint">sporting director</span>.<br />
                            Play like an <span className="text-fa-cream">FPL manager</span>.
                        </h1>
                        <p className="font-mono text-base text-white/70 mt-5 max-w-2xl leading-relaxed">
                            Analyzuj. Scoutuj. Rozhoduj. Hraj. Uč se. Fantasy Academy tě naučí pracovat s hráči,
                            daty a rozhodováním podobně jako skutečný fotbalový analytik, scout nebo sportovní ředitel.
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-8">
                            <Link to="/academy" data-testid="home-cta-academy"
                                  className="font-mono text-xs uppercase tracking-widest px-5 py-3 bg-fa-mint text-black font-bold hover:opacity-90 transition-opacity">
                                Začít v Academy
                            </Link>
                            <Link to="/lab" data-testid="home-cta-tools"
                                  className="font-mono text-xs uppercase tracking-widest px-5 py-3 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black transition-colors">
                                Prozkoumat nástroje
                            </Link>
                            {!user && (
                                <button type="button" onClick={login} data-testid="home-cta-login"
                                        className="font-mono text-xs uppercase tracking-widest px-4 py-3 text-white/60 hover:text-white transition-colors">
                                    Přihlásit se →
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="mt-12 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-widest" data-testid="home-cycle-strip">
                        {CYCLE.map((c, i) => (
                            <React.Fragment key={c.key}>
                                <span className="px-3 py-1.5 border border-fa-mint/40 text-fa-mint bg-fa-mint/5">{c.key}</span>
                                {i < CYCLE.length - 1 && <ArrowRight size={12} className="text-white/30" />}
                            </React.Fragment>
                        ))}
                    </div>
                </Section>

                {/* JAK FUNGUJE */}
                <Section>
                    <h2 className="font-display font-black uppercase text-lg md:text-xl mb-8 text-white/90">Jak Fantasy Academy funguje</h2>
                    <div className="grid md:grid-cols-5 gap-3" data-testid="home-how-it-works">
                        {CYCLE.map((c, i) => (
                            <div key={c.key} className="border border-white/10 bg-black/40 p-4 space-y-3 hover:border-fa-mint/40 transition-colors">
                                <div className="flex items-center gap-2">
                                    <span className="font-mono text-[9px] text-white/30">{i + 1}</span>
                                    <c.icon size={18} className="text-fa-mint" />
                                </div>
                                <div className="font-display font-bold uppercase text-sm">{c.key}</div>
                                <p className="font-mono text-[10px] text-white/55 leading-relaxed">{c.text}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* VÍCE NEŽ FANTASY HRA */}
                <Section>
                    <div className="border border-fa-violet/30 bg-fa-violet/5 p-6 md:p-10">
                        <h2 className="font-display font-black uppercase text-lg md:text-xl text-white/90">Více než fantasy hra</h2>
                        <p className="font-mono text-sm text-white/65 mt-3 max-w-3xl leading-relaxed">
                            Fantasy Premier League je simulátorem skutečné práce s hráči, rozpočtem, daty, rizikem
                            a dlouhodobým plánováním. Fantasy Academy tyto schopnosti rozvíjí a umožňuje uživatelům
                            vytvářet vlastní analytické portfolio.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-6" data-testid="home-skills">
                            {SKILLS.map((s) => (
                                <span key={s} className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/15 text-white/70">{s}</span>
                            ))}
                        </div>
                    </div>
                </Section>

                {/* CESTA DO FOTBALU */}
                <Section>
                    <div className="grid md:grid-cols-2 gap-8 items-start">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-cream mb-3">Připravovaný kariérní program</div>
                            <h2 className="font-display font-black uppercase text-lg md:text-xl text-white/90">Z Fantasy Academy do skutečného fotbalu</h2>
                            <p className="font-mono text-sm text-white/65 mt-4 leading-relaxed">
                                Nejlepší absolventi Fantasy Academy budou moci získat ověřený profil založený na jejich
                                dlouhodobých výsledcích, scoutingových reportech a kvalitě rozhodování. Naším cílem je
                                propojovat nejzajímavější absolventy s partnerskými fotbalovými kluby a organizacemi po celém světě.
                            </p>
                        </div>
                        <div className="border border-white/10 bg-black/40 p-5">
                            <div className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-3">Academy Profile — tvoje portfolio</div>
                            <div className="grid grid-cols-2 gap-2 font-mono text-[10px] text-white/70">
                                {["Manager Rating", "Knowledge XP", "Úspěšnost Recruitment Focusů", "Scoutingové reporty", "Kvalita predikcí", "Decision Review"].map((x) => (
                                    <div key={x} className="border-l-2 border-fa-mint/40 pl-2 py-1">{x}</div>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-4">
                                {["Player Scouting", "Data Analysis", "Recruitment", "Squad Planning", "Fixture Analysis", "Football Content"].map((s) => (
                                    <span key={s} className="font-mono text-[8px] uppercase px-2 py-0.5 bg-fa-violet/20 text-fa-sky">{s}</span>
                                ))}
                            </div>
                        </div>
                    </div>
                </Section>

                {/* NÁSTROJE */}
                <Section>
                    <h2 className="font-display font-black uppercase text-lg md:text-xl mb-8 text-white/90">Hlavní nástroje</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="home-tools">
                        {TOOLS.map((t) => (
                            <Link key={t.title} to={t.to}
                                  className="border border-white/10 bg-black/40 p-5 space-y-2 hover:border-fa-mint/50 hover:bg-fa-mint/5 transition-colors group">
                                <t.icon size={22} className="text-fa-mint" />
                                <div className="font-display font-bold uppercase text-sm group-hover:text-fa-mint transition-colors">{t.title}</div>
                                <p className="font-mono text-[10px] text-white/55 leading-relaxed">{t.text}</p>
                            </Link>
                        ))}
                    </div>
                </Section>

                {/* ACADEMY + FUN */}
                <Section>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Link to="/academy" className="border border-white/10 bg-black/40 p-6 hover:border-fa-cream/50 transition-colors group" data-testid="home-academy-card">
                            <GraduationCap size={24} className="text-fa-cream" />
                            <div className="font-display font-black uppercase text-base mt-3 group-hover:text-fa-cream transition-colors">Academy</div>
                            <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed">
                                Nauč se přemýšlet jako analytik, scout a sportovní ředitel. Články, návody,
                                scoutingové reporty, datová metodika, týdenní FPL obsah.
                            </p>
                        </Link>
                        <Link to="/fun" className="border border-white/10 bg-black/40 p-6 hover:border-fa-yellow/50 transition-colors group" data-testid="home-fun-card">
                            <GameController size={24} className="text-fa-yellow" />
                            <div className="font-display font-black uppercase text-base mt-3 group-hover:text-fa-yellow transition-colors">Fun</div>
                            <p className="font-mono text-[11px] text-white/60 mt-2 leading-relaxed">
                                Otestuj své znalosti Premier League a FPL. INKO, denní výzvy, Stat Battle,
                                Higher or Lower, žebříčky.
                            </p>
                        </Link>
                    </div>
                </Section>

                {/* FINAL CTA */}
                <Section>
                    <div className="text-center py-10 border border-fa-mint/30 bg-fa-mint/5">
                        <Trophy size={28} className="text-fa-mint mx-auto" />
                        <h2 className="font-display font-black uppercase text-lg md:text-xl mt-4">Začni budovat svůj manažerský profil</h2>
                        <div className="flex justify-center gap-3 mt-6">
                            {!user ? (
                                <button type="button" onClick={login} data-testid="home-final-signup"
                                        className="font-mono text-xs uppercase tracking-widest px-5 py-3 bg-fa-mint text-black font-bold hover:opacity-90">
                                    Vytvořit účet
                                </button>
                            ) : null}
                            <Link to={user ? "/dashboard" : "/lab"} data-testid="home-final-open"
                                  className="font-mono text-xs uppercase tracking-widest px-5 py-3 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black transition-colors">
                                Otevřít Fantasy Academy
                            </Link>
                        </div>
                    </div>
                </Section>
            </div>
        </AppShell>
    );
}
